/**
 * Shared utilities and constants for dashboard components.
 */
import { parseISO } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { useNavigate } from "react-router-dom";
import { BACKEND_URL as CONFIG_BACKEND_URL, API as CONFIG_API } from "../../config/api";

export const BACKEND_URL = CONFIG_BACKEND_URL;
export const API = CONFIG_API;

// Data cache to prevent excessive API calls
export const dataCache = {
  data: {},
  timestamps: {},
  CACHE_TTL: 30000, // 30 seconds cache TTL
  
  get(key) {
    const timestamp = this.timestamps[key];
    if (timestamp && Date.now() - timestamp < this.CACHE_TTL) {
      return this.data[key];
    }
    return null;
  },
  
  set(key, value) {
    this.data[key] = value;
    this.timestamps[key] = Date.now();
  },
  
  invalidate(key) {
    delete this.data[key];
    delete this.timestamps[key];
  },
  
  invalidateAll() {
    this.data = {};
    this.timestamps = {};
  }
};

// EST Timezone formatting helper
export const formatEST = (dateString, formatStr = 'MMM d, yyyy h:mm a') => {
  if (!dateString) return '-';
  try {
    let normalizedDate = dateString;
    if (typeof dateString === 'string' && !dateString.endsWith('Z') && !dateString.includes('+') && !dateString.includes('-', 10)) {
      normalizedDate = dateString + 'Z';
    }
    const date = typeof normalizedDate === 'string' ? parseISO(normalizedDate) : normalizedDate;
    return formatInTimeZone(date, 'America/New_York', formatStr);
  } catch {
    return '-';
  }
};

// Helper for Cmd/Ctrl+Click to open in new tab
export const handleLinkClick = (e, path, navigate) => {
  if (e.metaKey || e.ctrlKey) {
    window.open(path, '_blank');
  } else {
    navigate(path);
  }
};

// Reusable clickable link component that supports Cmd+Click
export function ClickableLink({ to, children, className = '', onClick }) {
  const navigate = useNavigate();
  
  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (onClick) {
      if (e.metaKey || e.ctrlKey) {
        window.open(to, '_blank');
      } else {
        onClick(e);
      }
    } else {
      if (e.metaKey || e.ctrlKey) {
        window.open(to, '_blank');
      } else {
        navigate(to);
      }
    }
  };
  
  return (
    <a 
      href={to} 
      onClick={handleClick}
      className={className}
    >
      {children}
    </a>
  );
}

// Kanban status order
export const STATUS_ORDER = ['Available', 'Pole Requested', 'Awaiting Pickup', 'Active', 'Broken'];

// Request status options
export const REQUEST_STATUS_OPTIONS = ['all', 'requested', 'confirmation pending', 'approved', 'awaiting pickup', 'completed', 'cancelled'];

// Status configuration with colors
export const STATUS_CONFIG = {
  'Broken': { bg: 'bg-red-50', border: 'border-red-200', headerBg: 'bg-red-100', text: 'text-red-700', badge: 'destructive', dot: 'bg-red-500' },
  'Active': { bg: 'bg-emerald-50', border: 'border-emerald-200', headerBg: 'bg-emerald-100', text: 'text-emerald-700', badge: 'default', dot: 'bg-emerald-500' },
  'Pole Requested': { bg: 'bg-blue-50', border: 'border-blue-200', headerBg: 'bg-blue-100', text: 'text-blue-700', badge: 'secondary', dot: 'bg-blue-500' },
  'Awaiting Pickup': { bg: 'bg-amber-50', border: 'border-amber-200', headerBg: 'bg-amber-100', text: 'text-amber-700', badge: 'outline', dot: 'bg-amber-500' },
  'Available': { bg: 'bg-gray-50', border: 'border-gray-200', headerBg: 'bg-gray-100', text: 'text-gray-700', badge: 'outline', dot: 'bg-gray-400' },
  'default': { bg: 'bg-slate-50', border: 'border-slate-200', headerBg: 'bg-slate-100', text: 'text-slate-700', badge: 'outline', dot: 'bg-slate-400' }
};

export const getStatusConfig = (status) => STATUS_CONFIG[status] || STATUS_CONFIG['default'];

export const parseLengthForSort = (length) => {
  if (!length) return 0;
  const match = String(length).match(/(\d+)'(\d+)?/);
  if (match) return (parseInt(match[1]) || 0) * 12 + (parseInt(match[2]) || 0);
  return 0;
};

export const parseWeightForSort = (weight) => {
  if (!weight) return 0;
  return parseInt(String(weight).replace(/[^\d]/g, '')) || 0;
};

// Format length for email (e.g., 11'6 -> 11'6")
export const formatLengthForEmail = (length) => {
  if (!length) return '';
  return length.includes('"') ? length : `${length}"`;
};
